import hasErrorsMixin from '@/common/mixins/hasErrorsMixin'
import moment from 'moment'
import { OrganizationSubjects } from '../../enums/OrganizationSubjects'
import { ApiClient } from '../../helpers/api-client/ApiClient'
import { DaData } from '../helpers/api-client/DaData'
import { Logger } from '../helpers/Logger'
import { ApplicationAccessLevels } from '../enums/ApplicationAccessLevels'

export default {
  mixins: [
    hasErrorsMixin
  ],

  computed: {
    disabledInput () {
      return Boolean(this.isCheck || this.isView)
    },

    isCheck () {
      return this.checkAccessLevel(ApplicationAccessLevels.Check)
    },

    isView () {
      return this.checkAccessLevel(ApplicationAccessLevels.View)
    },

    canAttachScanCopy () {
      return Number(this.configuration.can_attach_scancopy) === 1
    },

    isCheckOrView () {
      return this.isCheck || this.isView
    },

    isEdit () {
      return this.checkAccessLevel(ApplicationAccessLevels.Edit)
    },

    isDuplicate () {
      return this.checkAccessLevel(ApplicationAccessLevels.Duplicate)
    },

    isDraft () {
      return this.checkAccessLevel(ApplicationAccessLevels.Draft)
    },

    isNew () {
      return !this.isCheck && !this.isView && !this.isEdit && !this.isDuplicate && !this.isDraft
    },

    needAgentCheck () {
      return Number(this.form.application.need_agent_check) === 1
    },

    isEntityOrganizationSubject () {
      return this.form.organization.subject_id === OrganizationSubjects.Entity
    },

    isFizOrganizationSubject () {
      return this.form.organization.subject_id === OrganizationSubjects.Fiz
    },

    isIndividualOrganizationSubject () {
      return this.form.organization.subject_id === OrganizationSubjects.Individual
    },

    isGkoOrganizationSubject () {
      return this.form.organization.subject_id === OrganizationSubjects.Gko
    },

    isTerminalSubject () {
      return this.form.organization.subject_id === OrganizationSubjects.Terminal
    },

    isForeignSubject () {
      return this.form.organization.subject_id === OrganizationSubjects.Foreign
    }
  },

  methods: {
    checkAccessLevel (accessLevel) {
      return Boolean(
        (this.$route.query.application_id || this.$route.params.id) &&
        (this.$route.query.access_level === accessLevel || this.$route.params.mode === accessLevel)
      )
    },

    disableSubmitButton () {
      Logger.log('Disabling submit button...')
      this.$emit('disable-submit-button')
    },

    enableSubmitButton () {
      Logger.log('Enabling submit button...')
      this.$emit('enable-submit-button')
    },

    resetTypeaheadActiveIndex (typeaheadRef) {
      this.$nextTick(() => {
        if (!typeaheadRef) {
          return
        }

        if (typeaheadRef && Array.isArray(typeaheadRef)) {
          for (const ref in typeaheadRef) {
            if (typeaheadRef[ref]) {
              typeaheadRef[ref].activeIndex = 0
            }
          }
        } else {
          typeaheadRef.activeIndex = 0
        }
      })
    },

    async updateFIO (query) {
      let parts = ''
      const dbPersonalsTypeahead = []
      let fioList = []

      if (document.activeElement.id.includes('organization')) {
        parts = null
      } else if (document.activeElement.id.includes('lastname')) {
        parts = 'SURNAME'
      } else if (document.activeElement.id.includes('firstname')) {
        parts = 'NAME'
      } else if (document.activeElement.id.includes('name')) {
        parts = 'PATRONYMIC'
      }

      if (parts === 'SURNAME') {
        for (let i = 0; i < this.typeaheads.personals.length; i++) {
          dbPersonalsTypeahead[i] = {
            value: this.typeaheads.personals[i].search,
            data: this.typeaheads.personals[i]
          }
          dbPersonalsTypeahead[i].data.value = dbPersonalsTypeahead[i].data.search
        }

        let count = 0
        for (let i = 0; i < dbPersonalsTypeahead.length; i++) {
          if (dbPersonalsTypeahead[i].value && dbPersonalsTypeahead[i].value.indexOf(query) === 0) {
            fioList[count] = dbPersonalsTypeahead[i]
            count++
          }
        }
      }

      const fioDaData = await DaData.suggestFio(query, {
        parts: [parts]
      })

      fioList = fioList.concat(fioDaData.suggestions.map(item => ({ dadata: true, ...item })))

      return fioList
    },

    async typeaheadOrganizationName (query, isGroupBlock = true) {
      if (this.isFizOrganizationSubject) {
        return await this.updateFIO(query)
      }

      return await this.getOrganizationSuggestions(query, isGroupBlock)
    },

    async typeaheadOrganizationAddress (query) {
      return await this.typeaheadAddresses(query, false)
    },

    async typeaheadOrganizationInn (query, isGroupBlock = true) {
      return await this.getOrganizationSuggestions(query, isGroupBlock, true)
    },

    async getOrganizationSuggestions (query, isGroupBlock = true, inn = false) {
      let type = 'LEGAL'
      if (this.form.organization.subject_id === 'individual') {
        type = 'INDIVIDUAL'
      }

      const district = {
        nw: [
          { kladr_id: '29' },
          { kladr_id: '35' },
          { kladr_id: '39' },
          { kladr_id: '10' },
          { kladr_id: '11' },
          { kladr_id: '47' },
          { kladr_id: '51' },
          { kladr_id: '29' },
          { kladr_id: '53' },
          { kladr_id: '60' },
          { kladr_id: '78' }
        ],
        fe: [
          { kladr_id: '30' },
          { kladr_id: '14' },
          { kladr_id: '25' },
          { kladr_id: '27' },
          { kladr_id: '28' },
          { kladr_id: '41' },
          { kladr_id: '49' },
          { kladr_id: '65' },
          { kladr_id: '75' },
          { kladr_id: '79' },
          { kladr_id: '87' }
        ],
        volga: [
          { kladr_id: '2' },
          { kladr_id: '12' },
          { kladr_id: '13' },
          { kladr_id: '16' },
          { kladr_id: '18' },
          { kladr_id: '21' },
          { kladr_id: '43' },
          { kladr_id: '52' },
          { kladr_id: '56' },
          { kladr_id: '58' },
          { kladr_id: '59' },
          { kladr_id: '58' },
          { kladr_id: '59' },
          { kladr_id: '58' },
          { kladr_id: '63' },
          { kladr_id: '64' },
          { kladr_id: '73' }
        ],
        nk: [
          { kladr_id: '5' },
          { kladr_id: '6' },
          { kladr_id: '7' },
          { kladr_id: '9' },
          { kladr_id: '15' },
          { kladr_id: '20' },
          { kladr_id: '26' }
        ],
        sib: [
          { kladr_id: '4' },
          { kladr_id: '17' },
          { kladr_id: '19' },
          { kladr_id: '22' },
          { kladr_id: '24' },
          { kladr_id: '38' },
          { kladr_id: '42' },
          { kladr_id: '54' },
          { kladr_id: '55' },
          { kladr_id: '70' }
        ],
        ural: [
          { kladr_id: '45' },
          { kladr_id: '66' },
          { kladr_id: '72' },
          { kladr_id: '74' },
          { kladr_id: '86' },
          { kladr_id: '89' }
        ],
        center: [
          { kladr_id: '31' },
          { kladr_id: '32' },
          { kladr_id: '33' },
          { kladr_id: '36' },
          { kladr_id: '37' },
          { kladr_id: '40' },
          { kladr_id: '44' },
          { kladr_id: '46' },
          { kladr_id: '48' },
          { kladr_id: '50' },
          { kladr_id: '57' },
          { kladr_id: '62' },
          { kladr_id: '67' },
          { kladr_id: '68' },
          { kladr_id: '69' },
          { kladr_id: '71' },
          { kladr_id: '76' },
          { kladr_id: '77' }
        ],
        south: [
          { kladr_id: '1' },
          { kladr_id: '8' },
          { kladr_id: '23' },
          { kladr_id: '30' },
          { kladr_id: '34' },
          { kladr_id: '61' },
          { kladr_id: '91' },
          { kladr_id: '92' }
        ],
        all: []
      }

      this.locations = district[this.locationName]

      const organizationsRequest = await DaData.suggestParty(query, {
        type,
        locations: isGroupBlock ? this.locations : []
      })

      if (!organizationsRequest.suggestions || !Array.isArray(organizationsRequest.suggestions)) {
        return
      }

      const suggestions = organizationsRequest.suggestions.map(item => {
        if (inn) {
          item.value = item.data ? item.data.inn : null
        }

        return item
      })

      return suggestions
    },

    async suggestAddresses (query, onlyCity = false) {
      const data = {}

      if (onlyCity) {
        data.from_bound = {
          value: 'city'
        }

        data.to_bound = {
          value: 'city'
        }
      }

      const suggestions = await DaData.suggestAddress(query, data)

      return suggestions.suggestions ?? []
    },

    async typeaheadAddresses (query, onlyCity = false) {
      return await this.suggestAddresses(query, onlyCity) ?? []
    },

    checkVisitorOrganizationBlock () {
      const visitors = this.form?.visitors

      for (let i = 0; i < visitors?.length; i++) {
        this.checkVisitorOrganization(i)
      }
    },

    checkVisitorOrganization (visitorKey) {
      const visitorOrganizationInn = this.form?.visitors?.[visitorKey]?.personal?.organization_inn
      const appOrganizationInn = this.form?.organization?.inn
      const visitor = this.form?.visitors?.[visitorKey]?.personal

      visitor.visitorOrganizationErr = !!(visitorOrganizationInn === appOrganizationInn && appOrganizationInn && visitorOrganizationInn)

      for (let i = 0; i < this.form?.visitors?.length; i++) {
        if (this.form?.visitors?.[i]?.personal?.visitorOrganizationErr) {
          this.disableSubmitButton()

          break
        }

        this.enableSubmitButton()
      }
    },

    checkInnControlSum (visitorKey = null) {
      const inn = visitorKey !== null ? this.form?.visitors?.[visitorKey]?.personal?.organization_inn : this.form?.organization?.innData
      let isErr = false

      if (inn) {
        const innMain = inn.substr(0).split('')

        if (Number(inn.length) === 10) {
          const controlSum = ((2 * Number(innMain[0]) + 4 * Number(innMain[1]) + 10 * Number(innMain[2]) +
            3 * Number(innMain[3]) + 5 * Number(innMain[4]) + 9 * Number(innMain[5]) + 4 * Number(innMain[6]) +
            6 * Number(innMain[7]) + 8 * Number(innMain[8])) % 11) % 10

          isErr = Number(controlSum) !== Number(inn.substr(-1, 1))
        } else if (Number(inn.length) === 12) {
          const controlSum1 = ((7 * Number(innMain[0]) + 2 * Number(innMain[1]) + 4 * Number(innMain[2]) +
            10 * Number(innMain[3]) + 3 * Number(innMain[4]) + 5 * Number(innMain[5]) + 9 * Number(innMain[6]) +
            4 * Number(innMain[7]) + 6 * Number(innMain[8]) + 8 * Number(innMain[9])) % 11) % 10

          const controlSum2 = ((3 * Number(innMain[0]) + 7 * Number(innMain[1]) + 2 * Number(innMain[2]) +
            4 * Number(innMain[3]) + 10 * Number(innMain[4]) + 3 * Number(innMain[5]) + 5 * Number(innMain[6]) +
            9 * Number(innMain[7]) + 4 * Number(innMain[8]) + 6 * Number(innMain[9]) + 8 * Number(innMain[10])) % 11) % 10

          isErr = Number(controlSum1) !== Number(inn.substr(-2, 1)) && Number(controlSum2) !== Number(inn.substr(-1, 1))
        } else {
          isErr = true
        }
      }

      if (visitorKey !== null) {
        this.form.visitors[visitorKey].personal.innControlSumErr = isErr
      } else {
        this.form.organization.innControlSumErr = isErr
      }

      for (let i = 0; i < this.form?.visitors?.length; i++) {
        const err = this.form?.visitors?.[i]?.personal?.innControlSumErr || this.form?.organization?.innControlSumErr
        if (err) {
          this.innControlSumErr = err
          break
        } else {
          this.innControlSumErr = false
        }
      }
    },

    async checkVisitorsInPass (visitorKey) {
      if (this.form?.visitors?.[visitorKey]?.personal?.identification_number) {
        await ApiClient
          .checkCurrentVisitorApplications({
            visitor: this.form?.visitors?.[visitorKey]?.personal,
            application: this.form.application
          })
          .then(response => {
            if (response.data && response.data.message) {
              this.form.visitors[visitorKey].personal.visitorErr = response.data.message
            } else {
              this.form.visitors[visitorKey].personal.visitorErr = null
            }
          })
      } else {
        this.form.visitors[visitorKey].personal.visitorErr = null
      }
    },

    dateOptionConfigurator () {
      let date = moment('1799-12-31')

      if (!this.isCheck && !this.isView) {
        date = moment().add(-1, 'days')
      }

      return date.toDate()
    }
  }
}
